//
// Copyright 2026 DXOS.org
//
import { create, fromBinary, toBinary, } from '@bufbuild/protobuf';
import { AnySchema } from '@bufbuild/protobuf/wkt';
import { Stream } from '@dxos/async';
import { invariant } from '@dxos/invariant';
import { getAsyncProviderValue } from '@dxos/util';
import { bufRegistry } from './registry.js';
// Buf's descriptors replace protobuf.js's `pb.Service` here. The shapes on either side of the codec
// are unchanged, so `ServiceBundle` consumers and RPC handlers see the same values as before; see
// `docs/audits/protobufjs-to-buf.md` (`#8`).
/**
 * Legacy `Any.typeUrl` carried protobuf.js's `fullName`, which is dot-prefixed; buf's `typeName` is
 * not. `service-type-url.test.ts` establishes that no peer reads it on the service path, so the
 * dot-free form goes on the wire unchanged rather than being re-prefixed to imitate the old value.
 */
const typeUrlFor = (desc) => desc.typeName;
/**
 * Buf equivalent of the legacy `ServiceDescriptor`, structurally compatible with it so a service
 * bundle can hold either.
 */
export class BufServiceDescriptor {
    _service;
    #methods;
    constructor(_service) {
        this._service = _service;
    }
    get name() {
        return this._service.typeName;
    }
    get serviceDesc() {
        return this._service;
    }
    /** Builds a client whose methods encode onto `backend` and decode its responses. */
    createClient(backend) {
        const client = {};
        for (const method of this._service.methods) {
            // `localName` is the camelCase key protobuf.js derived by hand, so handler and client names
            // are unchanged by the switch.
            client[method.localName] = this.#methodStub(method, backend);
            Object.defineProperty(client[method.localName], 'name', { value: method.localName });
        }
        return client;
    }
    /** Builds a backend that decodes onto `handlers` and encodes what they return. */
    createServer(handlers) {
        return new BufServiceHandler(this._service, this.#methodCodecs(), handlers);
    }
    #methodStub(method, backend) {
        const codecs = this.#methodCodecs().get(method.name);
        invariant(codecs, `Method not found: ${method.name}`);
        const request = (value) => create(AnySchema, { value: toBinary(codecs.request, value), typeUrl: typeUrlFor(method.input) });
        if (method.methodKind === 'server_streaming') {
            return (value, options) => Stream.map(backend.callStream(method.name, request(value), options), (data) => fromBinary(codecs.response, data.value));
        }
        invariant(method.methodKind === 'unary', `Unsupported method kind: ${method.methodKind}`);
        return async (value, options) => {
            const response = await backend.call(method.name, request(value), options);
            return fromBinary(codecs.response, response.value);
        };
    }
    #methodCodecs() {
        return (this.#methods ??= new Map(this._service.methods.map((method) => [method.name, { method, request: method.input, response: method.output }])));
    }
}
/**
 * Buf equivalent of the legacy `ServiceHandler`.
 */
export class BufServiceHandler {
    _service;
    _methods;
    _handlers;
    constructor(_service, _methods, _handlers) {
        this._service = _service;
        this._methods = _methods;
        this._handlers = _handlers;
    }
    async call(methodName, request, options) {
        const { method, request: requestCodec, response: responseCodec } = this.#methodInfo(methodName);
        invariant(method.methodKind === 'unary', `Invalid RPC method call: response streaming mismatch. ${methodName}`);
        const handler = await this.#handler(method);
        const response = await handler(fromBinary(requestCodec, request.value), options);
        return create(AnySchema, {
            value: toBinary(responseCodec, response),
            typeUrl: typeUrlFor(method.output),
        });
    }
    callStream(methodName, request, options) {
        const { method, request: requestCodec, response: responseCodec } = this.#methodInfo(methodName);
        invariant(method.methodKind === 'server_streaming', `Invalid RPC method call: response streaming mismatch., ${methodName}`);
        const decoded = fromBinary(requestCodec, request.value);
        const responses = Stream.unwrapPromise(this.#handler(method).then((handler) => handler(decoded, options)));
        return Stream.map(responses, (data) => create(AnySchema, { value: toBinary(responseCodec, data), typeUrl: typeUrlFor(method.output) }));
    }
    async #handler(method) {
        const service = await getAsyncProviderValue(this._handlers);
        const handler = service[method.localName];
        invariant(handler, `Handler is missing: ${method.localName}`);
        return handler.bind(service);
    }
    #methodInfo(methodName) {
        const codecs = this._methods.get(methodName);
        invariant(codecs, `Method not found: ${methodName} on ${this._service.typeName}`);
        return codecs;
    }
}
/**
 * Buf-backed counterpart to `schema.getService()`, keyed by the same fully-qualified service name.
 */
export const getBufService = (typeName) => {
    const desc = bufRegistry.getService(typeName);
    invariant(desc, `Service not found in the buf registry: ${typeName}`);
    return new BufServiceDescriptor(desc);
};
//# sourceMappingURL=service.js.map